// Home page — pick a module from registry with 3D thumbnail preview
// Render grid cards with cached thumbnails or generate on first visit

// @ts-ignore
import * as THREE from 'three';

import { MODULES, getModule, paramsFromSchema, ModuleSchema } from './registry';
import { injectTheme, THEME } from './ui-framework';
import { fetchPreview, apiAuthHeaders, API_BASE } from './api-client';

// Thumbnail cache key (versioned to invalidate on module changes)
const THUMB_CACHE_KEY = 'boxed3d_thumbnails_v10_content_fingerprint';
const THUMB_EXPIRY_DAYS = 30;

interface ThumbnailCache {
  [moduleId: string]: {
    dataUrl: string;
    timestamp: number;
    fingerprint?: string;
  };
}

let homeScrollController: AbortController | null = null;

// Load cache from localStorage
function loadThumbnailCache(): ThumbnailCache {
  try {
    const cached = localStorage.getItem(THUMB_CACHE_KEY);
    if (!cached) return {};
    const data: ThumbnailCache = JSON.parse(cached);
    
    // Clean expired entries
    const now = Date.now();
    const expiryMs = THUMB_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    Object.keys(data).forEach(key => {
      const entry = data[key];
      if (entry && now - entry.timestamp > expiryMs) {
        delete data[key];
      }
    });
    
    // Save cleaned cache
    if (Object.keys(data).length > 0) {
      localStorage.setItem(THUMB_CACHE_KEY, JSON.stringify(data));
    }
    
    return data;
  } catch {
    return {};
  }
}

// Save thumbnail to cache
function saveThumbnailCache(moduleId: string, dataUrl: string, fingerprint?: string): void {
  try {
    const cache = loadThumbnailCache();
    cache[moduleId] = {
      dataUrl,
      timestamp: Date.now(),
      fingerprint,
    };
    localStorage.setItem(THUMB_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('[Thumbnail Cache] Failed to save:', err);
  }
}

// Get cached thumbnail, invalidating if module has been updated since cached
function getCachedThumbnail(moduleId: string, fingerprint?: string): string | null {
  try {
    const cache = loadThumbnailCache();
    const entry = cache[moduleId];
    if (!entry) return null;
    if (!fingerprint || entry.fingerprint !== fingerprint) return null;
    return entry.dataUrl || null;
  } catch {
    return null;
  }
}

// Render 3D model to canvas thumbnail (generate once & cache) with true multi-part colors.
//
// This runs for EVERY card on the home page at once. It must NOT share the module-level
// preview controller: fetchPreview's default path aborts the previous request on each new
// call ("latest preview wins"), so with the shared controller each card cancelled the card
// before it and only the last one ever finished - measured 2026-10-03, 5 of 8 thumbnails
// never regenerated. A dedicated controller keeps the renders independent. Nothing needs
// to abort a thumbnail: it is background work whose result is cached and saved.
async function generateThumbnail(moduleId: string, fingerprint?: string, bypassCache = false): Promise<string | null> {
  const ownController = new AbortController();
  try {
    const module = getModule(moduleId);
    if (!module) return null;

    // Check cache first (unless bypassed when static thumbnail was invalidated)
    if (!bypassCache) {
      const cached = getCachedThumbnail(moduleId, fingerprint);
      if (cached) return cached;
    }

    // Get default parameters
    const params = paramsFromSchema(module);

    // Fetch mesh data from API
    const meshData = await fetchPreview(moduleId, params, { signal: ownController.signal });
    if (!meshData || !meshData.triangles || meshData.triangles.length === 0) return null;

    // Create canvas & renderer
    const canvas = document.createElement('canvas');
    canvas.width = 320;
    canvas.height = 280;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(canvas.width, canvas.height);
    renderer.setClearColor(0xffffff, 1);
    renderer.setPixelRatio(1); // Use fixed pixel ratio for consistent caching

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      45,
      canvas.width / canvas.height,
      0.1,
      5000
    );
    camera.up.set(0, 0, 1);
    camera.position.set(150, -150, 120);
    camera.lookAt(0, 0, 0);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.7));
    const dl1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dl1.position.set(100, -150, 200);
    scene.add(dl1);
    const dl2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dl2.position.set(-100, 150, -100);
    scene.add(dl2);

    // Group triangles by groupId
    const groupMap = new Map<number, typeof meshData.triangles>();
    for (const tri of meshData.triangles) {
      const gId = tri.groupId ?? 0;
      if (!groupMap.has(gId)) {
        groupMap.set(gId, []);
      }
      groupMap.get(gId)!.push(tri);
    }

    // Helper to extract default hex color number for each groupId
    const getColorForGroup = (gId: number): number => {
      if (module.colorConfigs) {
        const cfg = module.colorConfigs.find(c => c.targetGroupIds?.includes(gId));
        if (cfg?.default) {
          return parseInt(cfg.default.replace('#', '0x'), 16);
        }
      }
      if (module.objects) {
        for (const obj of module.objects) {
          const part = obj.parts?.find(p => p.groupId === gId);
          const colorStr = part?.defaultColor || (part as any)?.color || obj.color;
          if (colorStr) {
            return parseInt(colorStr.replace('#', '0x'), 16);
          }
        }
      }
      const fallbacks = [0x8a9a6e, 0x475569, 0x1e293b, 0xcbd5e1];
      return fallbacks[gId % fallbacks.length] ?? 0x64748b;
    };

    const assemblyGroup = new THREE.Group();

    for (const [gId, tris] of groupMap.entries()) {
      const vertices: number[] = [];
      const normals: number[] = [];

      for (const tri of tris) {
        vertices.push(tri.v0.x, tri.v0.y, tri.v0.z);
        vertices.push(tri.v1.x, tri.v1.y, tri.v1.z);
        vertices.push(tri.v2.x, tri.v2.y, tri.v2.z);

        normals.push(tri.normal.x, tri.normal.y, tri.normal.z);
        normals.push(tri.normal.x, tri.normal.y, tri.normal.z);
        normals.push(tri.normal.x, tri.normal.y, tri.normal.z);
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(vertices), 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(normals), 3));
      geo.computeVertexNormals();

      const colorNum = getColorForGroup(gId);
      const mat = new THREE.MeshPhongMaterial({
        color: colorNum,
        side: THREE.DoubleSide,
        shininess: 45,
        specular: 0x444444,
      });

      const mesh = new THREE.Mesh(geo, mat);
      assemblyGroup.add(mesh);
    }

    scene.add(assemblyGroup);

    // Center & fit camera perfectly considering aspect ratio so tall/wide models never get clipped
    const bbox = new THREE.Box3().setFromObject(assemblyGroup);
    if (!bbox.isEmpty()) {
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const size = new THREE.Vector3();
      bbox.getSize(size);
      
      const aspect = canvas.width / canvas.height;
      const fovV = camera.fov * (Math.PI / 180);
      const fovH = 2 * Math.atan(Math.tan(fovV / 2) * aspect);
      
      // Calculate minimum distance needed for height and width independently
      const distV = (size.z / 2) / Math.tan(fovV / 2);
      const distH = (Math.max(size.x, size.y) / 2) / Math.tan(fovH / 2);
      
      // Use max required distance + 25% margin to fit all angles comfortably without clipping
      const margin = 1.28;
      const cameraDistance = Math.max(distV, distH) * margin;

      // Show the keychain loop face-on enough to read the hole and its attachment in the
      // card thumbnail; the generic iso angle makes this particular XY-plane ring look loose.
      const direction = moduleId === 'core_keychain_loop'
        ? new THREE.Vector3(0, -1, 1.15).normalize()
        : new THREE.Vector3(1, -1, 0.75).normalize();
      camera.position.copy(center).addScaledVector(direction, cameraDistance);
      camera.lookAt(center);
    }

    // Render
    renderer.render(scene, camera);

    // Convert to data URL & cache
    const dataUrl = canvas.toDataURL('image/png', 0.9);
    saveThumbnailCache(moduleId, dataUrl, fingerprint);

    // Clean up WebGL resources
    renderer.dispose();
    assemblyGroup.traverse((obj) => {
      if ((obj as THREE.Mesh).geometry) (obj as THREE.Mesh).geometry.dispose();
      if ((obj as THREE.Mesh).material) {
        const mat = (obj as THREE.Mesh).material;
        if (Array.isArray(mat)) mat.forEach(m => m.dispose());
        else mat.dispose();
      }
    });

    return dataUrl;
  } catch (err) {
    console.warn(`[Thumbnail] Failed to generate for ${moduleId}:`, err);
    return null;
  }
}

// LocalStorage Favorites helper
const FAVORITES_STORAGE_KEY = 'boxed3d_favorites';

function getFavorites(): string[] {
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function isFavorite(moduleId: string): boolean {
  return getFavorites().includes(moduleId);
}

function toggleFavorite(moduleId: string): boolean {
  try {
    const favorites = getFavorites();
    const index = favorites.indexOf(moduleId);
    let newState = false;
    if (index >= 0) {
      favorites.splice(index, 1);
      newState = false;
    } else {
      favorites.push(moduleId);
      newState = true;
    }
    localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    return newState;
  } catch (err) {
    console.warn('[Favorites] Failed to toggle favorite:', err);
    return false;
  }
}

// Create single module card component
function createModuleCard(mod: ModuleSchema, theme: typeof THEME, onSelect: (id: string) => void, onFavoriteToggle?: () => void): HTMLElement {
  const card = document.createElement('div');
  card.style.cssText = `
    background: white;
    border: 1px solid #e8e8e0;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
    display: flex;
    flex-direction: column;
    height: 100%;
  `;

  card.addEventListener('mouseenter', () => {
    card.style.borderColor = '#d0d0c8';
    card.style.transform = 'translateY(-6px)';
    card.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.08)';
  });

  card.addEventListener('mouseleave', () => {
    card.style.borderColor = '#e8e8e0';
    card.style.transform = '';
    card.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.03)';
  });

  card.addEventListener('click', () => onSelect(mod.id));

  // Thumbnail container
  const thumbnailContainer = document.createElement('div');
  thumbnailContainer.style.cssText = `
    width: 100%;
    height: 280px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid #e8e8e0;
  `;

  // Heart button for Quick Bookmark / Favoriting
  const favBtn = document.createElement('button');
  const favActive = isFavorite(mod.id);
  favBtn.innerHTML = favActive ? '♥' : '♡';
  favBtn.title = favActive ? 'Remove from Favorites' : 'Add to Favorites';
  favBtn.style.cssText = `
    position: absolute;
    top: 12px;
    left: 12px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid ${favActive ? '#ef4444' : 'rgba(203, 213, 225, 0.8)'};
    background: ${favActive ? '#ef4444' : 'rgba(255, 255, 255, 0.9)'};
    color: ${favActive ? '#ffffff' : '#ef4444'};
    font-size: 18px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 101;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  `;

  favBtn.addEventListener('click', (e) => {
    e.stopPropagation(); // Don't trigger card click
    const active = toggleFavorite(mod.id);
    favBtn.innerHTML = active ? '♥' : '♡';
    favBtn.title = active ? 'Remove from Favorites' : 'Add to Favorites';
    favBtn.style.background = active ? '#ef4444' : 'rgba(255, 255, 255, 0.9)';
    favBtn.style.color = active ? '#ffffff' : '#ef4444';
    favBtn.style.borderColor = active ? '#ef4444' : 'rgba(203, 213, 225, 0.8)';
    if (onFavoriteToggle) onFavoriteToggle();
  });

  thumbnailContainer.appendChild(favBtn);

  // Refresh button for forcing 3D thumbnail regeneration
  const refreshBtn = document.createElement('button');
  refreshBtn.innerHTML = '↻';
  refreshBtn.title = 'Regenerate 3D Thumbnail';
  refreshBtn.style.cssText = `
    position: absolute;
    top: 12px;
    left: 52px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(203, 213, 225, 0.8);
    background: rgba(255, 255, 255, 0.9);
    color: #475569;
    font-size: 18px;
    font-weight: bold;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 101;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  `;
  thumbnailContainer.appendChild(refreshBtn);

  // Helper: Create animated 3D Box illustration for loading / fallback
  const create3DBoxIllustration = (title: string, subtext: string) => {
    const boxContainer = document.createElement('div');
    boxContainer.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      user-select: none;
    `;

    boxContainer.innerHTML = `
      <style>
        @keyframes p3dBoxFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-7px); }
        }
        @keyframes p3dBoxShadow {
          0%, 100% { transform: scale(1); opacity: 0.16; }
          50% { transform: scale(0.68); opacity: 0.05; }
        }
        @keyframes p3dLidUnbox {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-6px) rotate(-4deg); }
        }
      </style>
      <div style="position:relative; width:80px; height:80px; display:flex; align-items:center; justify-content:center;">
        <svg width="76" height="76" viewBox="0 0 100 100" fill="none" style="overflow:visible;">
          <ellipse cx="50" cy="88" rx="26" ry="6" fill="#0f172a" style="animation: p3dBoxShadow 2.4s ease-in-out infinite; transform-origin: center;" />
          <g style="animation: p3dBoxFloat 2.4s ease-in-out infinite; transform-origin: center;">
            <path d="M20 40 L50 56 L50 82 L20 66 Z" fill="url(#p3d-box-left)" />
            <path d="M50 56 L80 40 L80 66 L50 82 Z" fill="url(#p3d-box-right)" />
            <path d="M50 56 L50 82" stroke="#ffffff" stroke-width="1.5" stroke-opacity="0.2" />
            <path d="M50 16 L80 32 L50 48 L20 32 Z" fill="url(#p3d-box-top)" style="animation: p3dLidUnbox 2.4s ease-in-out infinite; transform-origin: 50px 32px;" />
            <circle cx="50" cy="30" r="2.5" fill="#ffffff" fill-opacity="0.7" />
          </g>
          <defs>
            <linearGradient id="p3d-box-top" x1="20" y1="16" x2="80" y2="48" gradientUnits="userSpaceOnUse">
              <stop stop-color="#3b82f6" />
              <stop offset="1" stop-color="#2563eb" />
            </linearGradient>
            <linearGradient id="p3d-box-left" x1="20" y1="40" x2="50" y2="82" gradientUnits="userSpaceOnUse">
              <stop stop-color="#1e293b" />
              <stop offset="1" stop-color="#0f172a" />
            </linearGradient>
            <linearGradient id="p3d-box-right" x1="50" y1="40" x2="80" y2="82" gradientUnits="userSpaceOnUse">
              <stop stop-color="#334155" />
              <stop offset="1" stop-color="#1e293b" />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div style="margin-top:10px; font-size:11px; font-weight:700; color:#334155; letter-spacing:0.04em; text-align:center;">
        ${title}
      </div>
      <div style="margin-top:2px; font-size:10px; font-weight:500; color:#94a3b8; text-align:center;">
        ${subtext}
      </div>
    `;

    return boxContainer;
  };

  // Helper: auto-save rendered thumbnail dataUrl to backend
  const autoSaveThumbnail = (dataUrl: string, fingerprint?: string) => {
    fetch(`${API_BASE}/api/modules/${mod.id}/thumbnail`, {
      method: 'POST',
      headers: apiAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ image: dataUrl, fingerprint }),
    }).then(r => r.json()).then(res => {
      if (res.status === 'ok') {
        console.log(`[Thumbnail] Auto-saved static thumbnail for '${mod.id}' -> ${res.path}`);
      }
    }).catch(err => console.warn(`[Thumbnail] Failed to auto-save thumbnail for ${mod.id}:`, err));
  };

  const uidStr = mod.uniqueId || ('B3D_' + mod.id.slice(0, 5).toUpperCase());
  const badge = document.createElement('div');
  badge.className = 'b3d-id-badge';
  badge.textContent = uidStr;
  badge.style.cssText = `
    position: absolute;
    top: 12px;
    right: 12px;
    background: #0f172a;
    color: #38bdf8;
    font-family: monospace, sans-serif;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(56, 189, 248, 0.4);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    letter-spacing: 0.5px;
    z-index: 100;
    pointer-events: none;
  `;

  thumbnailContainer.appendChild(create3DBoxIllustration('Loading Preview...', 'Parametric Boxed3D Engine'));

  (async () => {
    const staticUrl = `${API_BASE}/modules/${mod.id}/thumbnail.png`;

    // Check if thumbnail needs regeneration
    const checkAndLoadThumbnail = async (forceRegen = false) => {
      try {
        // The loop has a module-specific front-facing presentation. Its stored server
        // thumbnail may still carry the old diagonal camera, even when the mesh fingerprint
        // has not changed, so invalidate that image once the presentation logic changes.
        const presentationNeedsRegen = mod.id === 'core_keychain_loop';
        const resp = await fetch(staticUrl, { method: 'HEAD' });
        const fingerprint = resp.headers.get('X-Thumbnail-Fingerprint') || undefined;
        const isStale = resp.headers.get('X-Thumbnail-Stale') === '1';
        const versionedStaticUrl = `${staticUrl}?v=${encodeURIComponent(fingerprint || mod.uniqueId || '1')}`;

        if (!forceRegen && !presentationNeedsRegen && resp.ok && !isStale) {
          showImage(versionedStaticUrl);
          return;
        }

        // A prior render with these exact inputs can repair a missing/stale disk thumbnail
        // without starting the C++ worker again.
        if (!forceRegen && !presentationNeedsRegen && fingerprint) {
          const cached = getCachedThumbnail(mod.id, fingerprint);
          if (cached) {
            showImage(cached);
            autoSaveThumbnail(cached, fingerprint);
            return;
          }
        }

        // Thumbnail is missing, stale, or forced -> Generate fresh WebGL 3D preview & save
        const dataUrl = await generateThumbnail(mod.id, fingerprint, true);
        if (dataUrl) {
          showImage(dataUrl);
          autoSaveThumbnail(dataUrl, fingerprint);
        } else if (!forceRegen) {
          showImage(versionedStaticUrl);
        } else {
          showBoxFallback();
        }
      } catch (err) {
        // Fallback: try generating locally if network or HEAD failed
        try {
          const dataUrl = await generateThumbnail(mod.id, undefined, true);
          if (dataUrl) {
            showImage(dataUrl);
            autoSaveThumbnail(dataUrl);
            return;
          }
        } catch {}
        showImage(staticUrl);
      }
    };

    refreshBtn.addEventListener('click', async (e) => {
      e.stopPropagation();
      refreshBtn.style.pointerEvents = 'none';
      refreshBtn.style.opacity = '0.6';
      refreshBtn.innerHTML = '⏳';
      try {
        await checkAndLoadThumbnail(true);
        refreshBtn.innerHTML = '✓';
        refreshBtn.style.color = '#16a34a';
        setTimeout(() => {
          refreshBtn.innerHTML = '↻';
          refreshBtn.style.color = '#475569';
          refreshBtn.style.opacity = '1';
          refreshBtn.style.pointerEvents = '';
        }, 1200);
      } catch {
        refreshBtn.innerHTML = '↻';
        refreshBtn.style.opacity = '1';
        refreshBtn.style.pointerEvents = '';
      }
    });

    const showImage = (src: string) => {
      const img = new Image();
      img.onload = () => {
        thumbnailContainer.innerHTML = '';
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.display = 'block';
        img.style.objectFit = 'contain';
        img.style.padding = '12px';
        img.style.boxSizing = 'border-box';
        thumbnailContainer.appendChild(favBtn);
        thumbnailContainer.appendChild(refreshBtn);
        thumbnailContainer.appendChild(img);
        thumbnailContainer.appendChild(badge);
      };
      img.onerror = () => showBoxFallback();
      img.src = src;
    };

    checkAndLoadThumbnail();

    function showBoxFallback() {
      thumbnailContainer.innerHTML = '';
      thumbnailContainer.appendChild(favBtn);
      thumbnailContainer.appendChild(refreshBtn);
      thumbnailContainer.appendChild(create3DBoxIllustration('Interactive 3D Model', 'Click to Open & Customize'));
      thumbnailContainer.appendChild(badge);
    }
  })();

  card.appendChild(thumbnailContainer);

  const cardContent = document.createElement('div');
  cardContent.style.cssText = `
    padding: 24px;
    flex: 1;
    display: flex;
    flex-direction: column;
  `;

  const cardTitle = document.createElement('h2');
  cardTitle.textContent = mod.label;
  cardTitle.style.cssText = `
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 4px;
    color: ${theme.text};
    margin: 0 0 8px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `;
  cardContent.appendChild(cardTitle);

  const specs = document.createElement('div');
  specs.style.cssText = `
    font-size: 11px;
    color: var(--text-dim);
    border-top: 1px solid #f0f0f0;
    padding-top: 12px;
    margin-top: auto;
  `;

  const specsItems = [];
  const sliderCount = mod.sliders?.length || 0;
  if (sliderCount > 0) specsItems.push(`${sliderCount} parameters`);
  const objectCount = mod.objects?.length || 0;
  if (objectCount > 0) specsItems.push(`${objectCount} parts`);
  const colorCount = mod.colorConfigs?.length || 0;
  if (colorCount > 0) specsItems.push(`${colorCount} colors`);

  specs.textContent = specsItems.join(' • ');
  cardContent.appendChild(specs);

  card.appendChild(cardContent);
  return card;
}

export function renderHome(
  rootEl: HTMLElement,
  onSelect: (moduleId: string) => void,
  scope: 'all' | 'core' = 'all',
): void {
  // The core_geometry scope is restricted to primitive wrap features (CG_XXXXX).
  // Core modules stay hidden from Design/Home unless a reusable surface primitive
  // explicitly opts in with showInDesign.
  const isCore = scope === 'core';
  const visibleModules = isCore
    ? MODULES.filter(m => m.category === 'core_geometry' || m.id.startsWith('core_'))
    : MODULES.filter(m => (m.category !== 'core_geometry' && !m.id.startsWith('core_')) || m.showInDesign === true);

  // R15: light theme only
  injectTheme();
  const theme = THEME;

  rootEl.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.style.cssText = `
    min-height: 100vh;
    width: 100vw;
    background: ${theme.bg};
    color: ${theme.text};
    font-family: ${theme.font};
    overflow-y: auto;
    overflow-x: hidden;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  `;

  // Header section
  const header = document.createElement('div');
  header.style.cssText = `
    position: relative;
    width: 100%;
    background: #ffffff;
    padding: 60px 20px;
    border-bottom: 1px solid ${theme.border};
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  `;

  const title = document.createElement('h1');
  title.textContent = isCore ? 'Design Library' : 'Boxed3D Design';
  title.style.cssText = `
    font-size: 38px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: ${theme.text};
    letter-spacing: -0.5px;
    text-align: center;
  `;
  header.appendChild(title);

  const subtitle = document.createElement('p');
  subtitle.textContent = isCore
    ? `Parametric Design Collection — ${visibleModules.length} model${visibleModules.length === 1 ? '' : 's'}`
    : 'Parametric 3D Modeling Platform';

  subtitle.style.cssText = `
    font-size: 16px;
    color: var(--text-muted);
    font-weight: 500;
    letter-spacing: 0.3px;
    margin: 0;
    text-align: center;
  `;
  header.appendChild(subtitle);

  // Navigation button between Home and the reusable design collection.
  if (!isCore) {
    const homeNavRow = document.createElement('div');
    homeNavRow.style.cssText = 'margin-top:18px;display:flex;align-items:center;justify-content:center;gap:10px;flex-wrap:wrap;';
    const coreBtn = document.createElement('button');
    coreBtn.type = 'button';
    coreBtn.style.cssText = `
      margin-top: 0;
      align-self: center;
      width: fit-content;
      max-width: fit-content;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 20px;
      font-size: 13px;
      font-weight: 600;
      font-family: inherit;
      color: #0f172a;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 9999px;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      transition: all 0.2s ease;
    `;
    coreBtn.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
        <line x1="12" y1="22.08" x2="12" y2="12"></line>
      </svg>
      <span>Design Library</span>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    `;
    coreBtn.addEventListener('mouseenter', () => {
      coreBtn.style.background = '#0f172a';
      coreBtn.style.color = '#ffffff';
      coreBtn.style.borderColor = '#0f172a';
      coreBtn.style.transform = 'translateY(-1px)';
      coreBtn.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.12)';
    });
    coreBtn.addEventListener('mouseleave', () => {
      coreBtn.style.background = '#f8fafc';
      coreBtn.style.color = '#0f172a';
      coreBtn.style.borderColor = '#cbd5e1';
      coreBtn.style.transform = 'translateY(0)';
      coreBtn.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
    });
    coreBtn.addEventListener('click', () => { window.location.hash = '#/core'; });
    homeNavRow.appendChild(coreBtn);
    const drawingBtn = document.createElement('button');
    drawingBtn.type = 'button';
    drawingBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18M15 9v3m-6 3h3"/></svg><span>2D Scale</span>';
    drawingBtn.title = 'Open 2D Image Scale Tool';
    drawingBtn.setAttribute('aria-label', 'Open 2D Image Scale Tool');
    drawingBtn.style.cssText = 'height:36px;padding:0 12px;display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1px solid #bfdbfe;border-radius:9999px;background:#eff6ff;color:#1d4ed8;font-family:inherit;font-size:12px;font-weight:600;white-space:nowrap;cursor:pointer;';
    drawingBtn.addEventListener('click', () => { window.location.hash = '#/drawing'; });
    homeNavRow.appendChild(drawingBtn);
    const flexiBtn = document.createElement('button');
    flexiBtn.type = 'button';
    flexiBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="M8.2 7.6 15.8 16.4M18 6h.01M6 18h.01"/><path d="M14 5h4v4M10 19H6v-4"/></svg><span>Flexi Builder</span>';
    flexiBtn.title = 'Open Flexi Builder';
    flexiBtn.setAttribute('aria-label', 'Open Flexi Builder');
    flexiBtn.style.cssText = 'height:36px;padding:0 12px;display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1px solid #a7f3d0;border-radius:9999px;background:#ecfdf5;color:#047857;font-family:inherit;font-size:12px;font-weight:600;white-space:nowrap;cursor:pointer;';
    flexiBtn.addEventListener('click', () => { window.location.hash = '#/flexi'; });
    homeNavRow.appendChild(flexiBtn);
    header.appendChild(homeNavRow);
  } else {
    const homeBtn = document.createElement('button');
    homeBtn.type = 'button';
    homeBtn.style.cssText = `
      margin-top: 18px;
      align-self: center;
      width: fit-content;
      max-width: fit-content;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 20px;
      font-size: 13px;
      font-weight: 600;
      font-family: inherit;
      color: #0f172a;
      background: #f8fafc;
      border: 1px solid #cbd5e1;
      border-radius: 9999px;
      cursor: pointer;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
      transition: all 0.2s ease;
    `;
    homeBtn.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7"/>
      </svg>
      <span>Back to Home</span>
    `;
    homeBtn.addEventListener('mouseenter', () => {
      homeBtn.style.background = '#0f172a';
      homeBtn.style.color = '#ffffff';
      homeBtn.style.borderColor = '#0f172a';
      homeBtn.style.transform = 'translateY(-1px)';
      homeBtn.style.boxShadow = '0 4px 12px rgba(15, 23, 42, 0.12)';
    });
    homeBtn.addEventListener('mouseleave', () => {
      homeBtn.style.background = '#f8fafc';
      homeBtn.style.color = '#0f172a';
      homeBtn.style.borderColor = '#cbd5e1';
      homeBtn.style.transform = 'translateY(0)';
      homeBtn.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
    });
    homeBtn.addEventListener('click', () => { window.location.hash = ''; });
    header.appendChild(homeBtn);
  }

  wrap.appendChild(header);

  // Content container (centers the whole page)
  const content = document.createElement('div');
  content.style.cssText = `
    width: 100%;
    max-width: 1600px;
    padding: 40px 40px 60px 40px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `;

  // Search & Sort Bar Container
  const toolbar = document.createElement('div');
  toolbar.style.cssText = `
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    gap: 16px;
    flex-wrap: wrap;
  `;

  // Search Input
  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.placeholder = '🔍 Search designs by name...';
  searchInput.style.cssText = `
    flex: 1;
    min-width: 280px;
    max-width: 480px;
    padding: 10px 16px;
    font-size: 14px;
    border: 1px solid ${theme.border};
    border-radius: 8px;
    outline: none;
    font-family: inherit;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    transition: border-color 0.2s;
  `;
  searchInput.addEventListener('focus', () => {
    searchInput.style.borderColor = '#0f172a';
  });
  searchInput.addEventListener('blur', () => {
    searchInput.style.borderColor = theme.border;
  });

  // Sort Controls Container
  const sortBox = document.createElement('div');
  sortBox.style.cssText = `
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: var(--text-muted);
  `;

  const sortLabel = document.createElement('span');
  sortLabel.textContent = 'Sort by:';
  sortLabel.style.fontWeight = '500';

  const sortSelect = document.createElement('select');
  sortSelect.style.cssText = `
    padding: 9px 14px;
    font-size: 13px;
    font-weight: 600;
    border: 1px solid ${theme.border};
    border-radius: 8px;
    background: #ffffff;
    color: ${theme.text};
    cursor: pointer;
    outline: none;
  `;

  const optFavorites = document.createElement('option');
  optFavorites.value = 'favorites';
  optFavorites.textContent = '⭐ Favorites First';

  const optNewest = document.createElement('option');
  optNewest.value = 'recently_updated';
  optNewest.textContent = '⚡ Mới cập nhật trước';
  optNewest.selected = true;

  const optRecentlyUpdated = document.createElement('option');
  optRecentlyUpdated.value = 'newest';
  optRecentlyUpdated.textContent = '🔄 Mới tạo trước';

  const optOldest = document.createElement('option');
  optOldest.value = 'oldest';
  optOldest.textContent = '⌛ Oldest First';

  const optNameAZ = document.createElement('option');
  optNameAZ.value = 'name_az';
  optNameAZ.textContent = '🔤 Name A - Z';

  const optNameZA = document.createElement('option');
  optNameZA.value = 'name_za';
  optNameZA.textContent = '🔤 Name Z - A';

  const optParamsCount = document.createElement('option');
  optParamsCount.value = 'params_desc';
  optParamsCount.textContent = '🎛️ Most Parameters';

  sortSelect.appendChild(optFavorites);
  sortSelect.appendChild(optNewest);
  sortSelect.appendChild(optRecentlyUpdated);
  sortSelect.appendChild(optOldest);
  sortSelect.appendChild(optNameAZ);
  sortSelect.appendChild(optNameZA);
  sortSelect.appendChild(optParamsCount);

  sortBox.appendChild(sortLabel);
  sortBox.appendChild(sortSelect);

  const searchGroup = document.createElement('div');
  searchGroup.style.cssText = 'flex:1;min-width:280px;max-width:640px;display:flex;align-items:center;gap:8px;';
  searchGroup.appendChild(searchInput);
  toolbar.appendChild(searchGroup);
  toolbar.appendChild(sortBox);
  content.appendChild(toolbar);

  // Category Filter Tabs Bar (Scalable for 1000 modules)
  let currentCategory = 'all';
  const categoryContainer = document.createElement('div');
  categoryContainer.style.cssText = `
    width: 100%;
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    overflow-x: auto;
    padding-bottom: 6px;
  `;

  // TASK 16 (2026-09-30) - CATEGORY FILTER FIX.
  //
  // The tab ids used to be `lamps` / `keychains` / `mechanical`, but the registry declares
  // module categories as `lighting` / `fidget` / `Accessories` / `gadgets`. The filter
  // compared them as exact strings, so the mismatch made those tabs permanently empty.
  // Measured against the registry as it stands:
  //     lighting    2 modules  (e27_led_base, pillow_chadao)
  //     fidget      2 modules  (keycap_clicker, name_clicker)
  //     Accessories 1 module   (text_keychain)
  // So clicking "Lamps & Lighting" or "Keychains & Tags" or "Mechanical & Tools" showed
  // NOTHING, and the two `fidget` modules were unreachable from every tab - they could only
  // be seen under "All Modules".
  //
  // (cg_demo used to be the one `gadgets` module; it has since been split into the 27
  // standalone core_* modules under the `core_geometry` category, which has its own page
  // at #/core and its own scope below.)
  //
  // Each tab now maps to a SET of real category values instead of a single string, so a
  // module can legitimately belong to more than one tab (e.g. a lamp-shaped keychain) and
  // new categories can be added here without touching the filter logic. Matching is
  // case-insensitive because the registry is inconsistent (`Accessories` is capitalised
  // while every other value is lower-case).
  const CATEGORY_TABS: Array<{ id: string; label: string; categories: string[] }> = [
    { id: 'all', label: 'All Modules', categories: [] },
    { id: 'favorites', label: '⭐ My Favorites', categories: [] },
    // 'lamp' và 'lamp_assembly' là các category dùng cho shade/assembly modules
    // (squiggle_angled_lamp, bambu_ribbed_pumpkin_lamp).
    // 'lighting' dùng cho complete lamp modules (elumara, solara, waves, youtube_short_spiral...).
    { id: 'lamps', label: '💡 Lamps & Lighting', categories: ['lighting', 'lamp', 'lamp_assembly'] },
    { id: 'gadgets', label: '⚙️ Gadgets & Accessories', categories: ['gadgets', 'accessories'] },
    // 'Accessories' (viết hoa) dùng trong chess_medals; match case-insensitive ở filter bên dưới.
    { id: 'keychains', label: '🔑 Keychains & Tags', categories: ['accessories'] },
    { id: 'fidget', label: '🎲 Fidget & Clickers', categories: ['fidget'] },
    { id: 'mechanical', label: '🔩 Mechanical & Tools', categories: ['mechanical', 'tools'] },
  ];

  const categories = isCore ? [] : CATEGORY_TABS;

  const catBtns: Map<string, HTMLButtonElement> = new Map();

  categories.forEach((cat) => {
    const btn = document.createElement('button');
    btn.textContent = cat.label;
    btn.style.cssText = `
      padding: 7px 16px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 9999px;
      border: 1px solid ${cat.id === 'all' ? '#0f172a' : theme.border};
      background: ${cat.id === 'all' ? '#0f172a' : '#ffffff'};
      color: ${cat.id === 'all' ? '#ffffff' : theme.text};
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.15s ease;
    `;
    btn.addEventListener('click', () => {
      currentCategory = cat.id;
      catBtns.forEach((b, k) => {
        if (k === currentCategory) {
          b.style.background = '#0f172a';
          b.style.color = '#ffffff';
          b.style.borderColor = '#0f172a';
        } else {
          b.style.background = '#ffffff';
          b.style.color = theme.text;
          b.style.borderColor = theme.border;
        }
      });
      renderFilteredModules();
    });
    catBtns.set(cat.id, btn);
    categoryContainer.appendChild(btn);
  });

  content.appendChild(categoryContainer);

  // Grid: fixed 4 columns, top-down flow
  const grid = document.createElement('div');
  grid.style.cssText = `
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 32px;
    width: 100%;
    margin: 0 auto;
    padding: 0;
  `;
  content.appendChild(grid);

  // Filter & Sort Logic Function
  const renderFilteredModules = () => {
    grid.innerHTML = '';
    const query = searchInput.value.trim().toLowerCase();

    let list = [...visibleModules];

    // Filter search by title, id, uid, desc, category, tags
    if (query) {
      list = list.filter(m => {
        const title = (m.label || '').toLowerCase();
        const id = (m.id || '').toLowerCase();
        const uid = (m.uniqueId || '').toLowerCase();
        const desc = (m.description || '').toLowerCase();
        const cat = (m.category || '').toLowerCase();
        const tagsStr = (m.tags || []).join(' ').toLowerCase();
        return title.includes(query) || id.includes(query) || uid.includes(query) || desc.includes(query) || cat.includes(query) || tagsStr.includes(query);
      });
    }

    // Filter by Selected Category Tab. The core_geometry scope renders no tabs at all
    // (its modules share one category), so `currentCategory` stays 'all' there.
    if (!isCore && currentCategory === 'favorites') {
      const favorites = getFavorites();
      list = list.filter(m => favorites.includes(m.id));
    } else if (!isCore && currentCategory !== 'all') {
      const tab = CATEGORY_TABS.find(t => t.id === currentCategory);
      const wanted = (tab?.categories ?? []).map(c => c.toLowerCase());
      // A tab with no categories mapped (a typo, or a tab added without a mapping) would
      // silently show nothing - the exact bug this task fixes. Fail loudly in dev instead.
      if (wanted.length === 0) {
        console.warn(`[home] Category tab "${currentCategory}" has no categories mapped - it will show nothing.`);
      }
      list = list.filter(m => {
        const cat = (m.category || 'gadgets').toLowerCase();
        return wanted.includes(cat);
      });
    }

    // Sort logic based on ISO timestamps / favorites
    const sortType = sortSelect.value;
    if (sortType === 'favorites') {
      const favorites = getFavorites();
      list.sort((a, b) => {
        const isFavA = favorites.includes(a.id) ? 1 : 0;
        const isFavB = favorites.includes(b.id) ? 1 : 0;
        if (isFavA !== isFavB) return isFavB - isFavA;
        return a.label.localeCompare(b.label);
      });
    } else if (sortType === 'newest') {
      list.sort((a, b) => {
        const timeA = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const timeB = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return timeB - timeA;
      });
    } else if (sortType === 'recently_updated') {
      list.sort((a, b) => {
        const timeA = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const timeB = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return timeB - timeA;
      });
    } else if (sortType === 'oldest') {
      list.sort((a, b) => {
        const timeA = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const timeB = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return timeA - timeB;
      });
    } else if (sortType === 'name_az') {
      list.sort((a, b) => a.label.localeCompare(b.label));
    } else if (sortType === 'name_za') {
      list.sort((a, b) => b.label.localeCompare(a.label));
    } else if (sortType === 'params_desc') {
      list.sort((a, b) => (b.sliders?.length || 0) - (a.sliders?.length || 0));
    }

    if (list.length === 0) {
      const emptyState = document.createElement('div');
      emptyState.style.cssText = `
        grid-column: 1 / -1;
        padding: 60px 20px;
        text-align: center;
        color: var(--text-muted);
        font-size: 15px;
      `;
      emptyState.textContent = 'No 3D modules found matching your search query.';
      grid.appendChild(emptyState);
      return;
    }

    for (const mod of list) {
      const card = createModuleCard(mod, theme, onSelect, () => {
        if (sortSelect.value === 'favorites') {
          renderFilteredModules();
        }
      });
      grid.appendChild(card);
    }
  };

  searchInput.addEventListener('input', renderFilteredModules);
  sortSelect.addEventListener('change', renderFilteredModules);

  // Initial render (Default: Newest First)
  renderFilteredModules();

  wrap.appendChild(content);
  rootEl.appendChild(wrap);

  const toTopButton = document.createElement('button');
  toTopButton.type = 'button';
  toTopButton.setAttribute('aria-label', 'Scroll to top');
  toTopButton.title = 'Scroll to top';
  toTopButton.style.cssText = `
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
    width: 46px;
    height: 46px;
    margin: 0;
    padding: 0;
    display: flex;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    align-items: center;
    justify-content: center;
    border: 1px solid ${theme.border};
    border-radius: 50%;
    background: #ffffff;
    color: ${theme.text};
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.16);
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  `;
  toTopButton.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6"/><path d="M12 9v12"/></svg>';
  toTopButton.style.transition += ', opacity 0.2s ease, visibility 0.2s ease';
  homeScrollController?.abort();
  homeScrollController = new AbortController();
  const scrollSignal = homeScrollController.signal;
  const updateToTopVisibility = () => {
    const isScrolled = Math.max(rootEl.scrollTop, wrap.scrollTop, window.scrollY) > 120;
    toTopButton.style.opacity = isScrolled ? '1' : '0';
    toTopButton.style.visibility = isScrolled ? 'visible' : 'hidden';
    toTopButton.style.pointerEvents = isScrolled ? 'auto' : 'none';
  };
  rootEl.addEventListener('scroll', updateToTopVisibility, { passive: true, signal: scrollSignal });
  wrap.addEventListener('scroll', updateToTopVisibility, { passive: true, signal: scrollSignal });
  window.addEventListener('scroll', updateToTopVisibility, { passive: true, signal: scrollSignal });
  updateToTopVisibility();
  toTopButton.addEventListener('click', () => {
    rootEl.scrollTo({ top: 0, behavior: 'smooth' });
    wrap.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
  toTopButton.addEventListener('mouseenter', () => {
    toTopButton.style.transform = 'translateY(-2px)';
    toTopButton.style.boxShadow = '0 7px 18px rgba(15, 23, 42, 0.2)';
  });
  toTopButton.addEventListener('mouseleave', () => {
    toTopButton.style.transform = 'translateY(0)';
    toTopButton.style.boxShadow = '0 4px 14px rgba(15, 23, 42, 0.16)';
  });
  rootEl.appendChild(toTopButton);
}
